import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRightLeft, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { categoryLabel } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

const SUGGESTED = ["Skill level better suited to this category", "Requested by the player", "Category merged or cancelled", "Gender/division correction"];

export default function TransferDialog({ tid, reg, cats, onClose, onDone }) {
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [full, setFull] = useState(null);
  if (!reg) return null;
  const src = cats.find((c) => c.id === reg.category_id) || {};
  const options = cats.filter((c) => c.id !== reg.category_id && c.discipline === src.discipline);

  const submit = async (force = false) => {
    if (!target) return toast.error("Choose a category to transfer to");
    if (reason.trim().length < 3) return toast.error("Enter the reason for the transfer");
    setBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${tid}/registrations/${reg.registration_id}/transfer`, { category_id: target, reason: reason.trim(), force });
      toast.success(`Transferred${data.over_limit ? " (over the category limit)" : ""} — the player was emailed the reason`);
      setFull(null); onDone(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "CATEGORY_FULL") setFull(d);
      else toast.error(formatApiError(d) || "Transfer failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={!!reg && !full} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="glass" style={{ background: "var(--surface)", color: "var(--text)" }} data-testid="transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-primary-c" /> Transfer entry</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              Move <b>{reg.player_name}{reg.partner?.name ? ` / ${reg.partner.name}` : ""}</b> from {categoryLabel(src)} to another {src.discipline} category. They'll get an email with your reason.
            </DialogDescription>
          </DialogHeader>
          {options.length === 0 ? <p className="text-sm" style={{ color: "#eab308" }} data-testid="transfer-no-options">No other {src.discipline} categories to transfer to.</p> : (
            <div className="space-y-3">
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="transfer-target" className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue placeholder="Target category" /></SelectTrigger>
                <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                  {options.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`transfer-opt-${c.id}`}>{categoryLabel(c)}</SelectItem>)}
                </SelectContent>
              </Select>
              <textarea data-testid="transfer-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
                placeholder="Reason for transfer (sent to the player)" className="w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} />
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTED.map((s, i) => <button key={s} type="button" data-testid={`transfer-suggest-${i}`} onClick={() => setReason(s)} className="text-[11px] glass rounded-full px-2.5 py-1" style={{ color: "var(--text-2)" }}>{s}</button>)}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button data-testid="transfer-cancel" onClick={onClose} className="glass rounded-full px-4 py-2 text-sm font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="transfer-submit" disabled={busy || !options.length} onClick={() => submit(false)} className="btn-glow rounded-full px-5 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Transfer & email
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!full} onOpenChange={(o) => !o && setFull(null)}>
        <DialogContent className="glass" style={{ background: "var(--surface)", color: "var(--text)" }} data-testid="transfer-full-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: "#eab308" }}><AlertTriangle className="w-5 h-5" /> Category is full</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }} data-testid="transfer-full-msg">
              {categoryLabel(cats.find((c) => c.id === target) || {})} already has {full?.taken}/{full?.max} entries. Transferring will put it over the limit. Do you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button data-testid="transfer-full-cancel" onClick={() => setFull(null)} className="glass rounded-full px-4 py-2 text-sm font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="transfer-full-proceed" disabled={busy} onClick={() => submit(true)} className="btn-glow rounded-full px-5 py-2 text-sm font-bold flex items-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Proceed anyway</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
